import type { CSSProperties } from 'react';
import { Link } from '@tanstack/react-router';
import { Section } from '~/components/layout';
import { ButtonLink } from '~/components/ui';
import { getAppCta } from '~/content/navigation';
import type { PlanArt, PlanCard, PlanFeature } from '~/content/pricing';
import { cn } from '~/lib/cn';
import { useContent, useLocalizedPath } from '~/lib/useLanguage';

const ASSETS = '/images/pricing';

type CardKind = 'platinum' | 'infinite';

/**
 * Card positions inside the 320×180 art band (Figma "Choose Your Plan"). `lift`
 * is how far each card rises on hover (negative sinks), so a stack fans apart without
 * leaving the band.
 */
const ART_LAYOUT: Record<PlanArt, { kind: CardKind; left: number; top: number; lift: number }[]> = {
  standard: [{ kind: 'platinum', left: 20, top: 64, lift: 12 }],
  pro: [{ kind: 'platinum', left: 20, top: 64, lift: 12 }],
  prestige: [{ kind: 'infinite', left: 20, top: 64, lift: 12 }],
};

/** A rotated, blended glow layer, positioned in the 280×164 card. */
function Glow({
  src,
  box,
  inner,
  inset,
}: {
  src: string;
  box: { left: number; top: number; size: number };
  inner: { width: number; height: number };
  inset: string;
}) {
  return (
    <div
      className="absolute flex items-center justify-center mix-blend-plus-lighter"
      style={{ left: box.left, top: box.top, width: box.size, height: box.size }}>
      <div className="flex-none rotate-45">
        <div className="relative" style={{ width: inner.width, height: inner.height }}>
          <img alt="" src={src} className="absolute block max-w-none" style={{ inset }} />
        </div>
      </div>
    </div>
  );
}

/** One 280×164 Visa card, layered as in the Figma "Card" component. */
function VisaCard({ kind, style }: { kind: CardKind; style: CSSProperties }) {
  const infinite = kind === 'infinite';
  return (
    <div
      className="absolute h-[164px] w-[280px] overflow-clip rounded-[20px] shadow-[0_0_9.566px_rgba(0,0,0,0.12),0_0_6.377px_rgba(0,0,0,0.08)] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-y-[calc(var(--lift)*-1)] group-hover:[transition-delay:var(--delay)] group-focus-within:translate-y-[calc(var(--lift)*-1)] group-hover:hover:translate-y-[calc(var(--lift)*-1-8px)] group-hover:hover:scale-[1.03] group-hover:hover:-rotate-1 group-hover:hover:[transition-delay:0ms] motion-reduce:transition-none"
      style={style}>
      {!infinite && <div className="absolute inset-0 bg-red-500" />}
      <img
        alt=""
        src={`${ASSETS}/card-texture.webp`}
        className="absolute top-[-5.58px] left-[-113.19px] h-[278.2px] w-[392.99px] max-w-none object-cover"
      />
      <div
        className={cn(
          'absolute top-0 left-0 h-[164.2px] w-[280px]',
          infinite
            ? 'bg-gradient-to-b from-[#f4c47e] via-[#e1bc84] to-[#c9a064]'
            : 'bg-gradient-to-b from-[#d60505] to-[#960303]',
        )}
      />
      {infinite && (
        <div className="absolute top-[-95.7px] left-[-52.8px] flex h-[220.5px] w-[265px] items-center justify-center">
          <div className="h-[248.5px] w-[103.1px] flex-none rotate-[57.48deg] bg-gradient-to-r from-black to-transparent opacity-30" />
        </div>
      )}
      <div className="absolute top-[-139.5px] left-[0.8px] flex h-[473.5px] w-[279px] items-center justify-center mix-blend-multiply">
        <img
          alt=""
          src={`${ASSETS}/card-grain.webp`}
          className="h-[279px] w-[473.5px] max-w-none flex-none rotate-90 object-cover"
        />
      </div>
      {infinite ? (
        <>
          <Glow
            src={`${ASSETS}/infinite-glow-lg.svg`}
            box={{ left: 38.76, top: -103.3, size: 256.84 }}
            inner={{ width: 64.94, height: 298.29 }}
            inset="-25.21% -115.8%"
          />
          <Glow
            src={`${ASSETS}/infinite-glow-sm.svg`}
            box={{ left: 143.2, top: -80.8, size: 130.39 }}
            inner={{ width: 23.12, height: 161.27 }}
            inset="-46.63% -325.19%"
          />
        </>
      ) : (
        <>
          <Glow
            src={`${ASSETS}/platinum-glow-lg.svg`}
            box={{ left: 51.02, top: -72.7, size: 241.98 }}
            inner={{ width: 46.8, height: 295.4 }}
            inset="-25.37% -160.1%"
          />
          <Glow
            src={`${ASSETS}/platinum-glow-sm.svg`}
            box={{ left: 154.67, top: -64.29, size: 129.92 }}
            inner={{ width: 23.04, height: 160.7 }}
            inset="-46.63% -325.19%"
          />
        </>
      )}
      <img
        alt=""
        src={`${ASSETS}/visa-wordmark.webp`}
        className="absolute top-[18.33px] left-[205.66px] h-[17.54px] w-[54.13px] max-w-none object-cover"
      />
      {infinite ? (
        <img
          alt=""
          src={`${ASSETS}/visa-infinite.svg`}
          className="absolute block max-w-none"
          style={{ inset: '25.79% 7.11% 70.71% 84.29%' }}
        />
      ) : (
        <div className="absolute top-[42.25px] left-[228.78px] h-[5.85px] w-[31.38px] overflow-clip">
          <img
            alt=""
            src={`${ASSETS}/platinum-label-a.svg`}
            className="absolute inset-0 block max-w-none"
          />
          <img
            alt=""
            src={`${ASSETS}/platinum-label-b.svg`}
            className="absolute inset-0 block max-w-none"
          />
        </div>
      )}
      <img
        alt=""
        src={`${ASSETS}/nsave-mark.svg`}
        className="absolute top-[15.94px] left-[15.94px] h-[18.33px] w-[19.13px] max-w-none"
      />
      <div className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0_-0.797px_3.189px_rgba(0,0,0,0.25)]" />
    </div>
  );
}

function PlanCardArt({ art, promotion }: { art: PlanArt; promotion?: string }) {
  return (
    <div className="relative h-[180px] overflow-clip bg-grey-25">
      <div
        aria-hidden="true"
        className="absolute top-0 left-1/2 h-[180px] w-[320px] -translate-x-1/2">
        {ART_LAYOUT[art].map((card, i) => (
          <VisaCard
            key={i}
            kind={card.kind}
            style={
              {
                left: card.left,
                top: card.top,
                '--lift': `${card.lift}px`,
                '--delay': `${i * 40}ms`,
              } as CSSProperties
            }
          />
        ))}
      </div>
      {promotion && (
        <span className="absolute top-4 left-1/2 -translate-x-1/2 rounded-pill bg-red-500 px-3 py-1.5 text-[14px] leading-[1.2] font-semibold whitespace-nowrap text-white">
          {promotion}
        </span>
      )}
    </div>
  );
}

function FeatureList({ features, muted = false }: { features: PlanFeature[]; muted?: boolean }) {
  const localizedPathFor = useLocalizedPath();
  return (
    <ul className={cn('mb-9 flex flex-col gap-6', !muted && 'mt-6')}>
      {features.map((feature) => (
        <li key={feature.text} className={cn('flex items-center gap-4', muted && 'opacity-50')}>
          <img alt="" src={`${ASSETS}/check.svg`} className="h-[10.16px] w-[12.48px] shrink-0" />
          <span className="text-[16px] leading-[1.16] font-medium tracking-[-0.032px] text-grey-900">
            {feature.text}
            {feature.link && (
              <>
                {' '}
                <Link
                  to={localizedPathFor(feature.link.href)}
                  className="text-red-500 transition-colors hover:text-red-700">
                  {feature.link.label}
                </Link>
              </>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function PlanCardItem({ plan }: { plan: PlanCard }) {
  const { ui } = useContent();
  const included = plan.features.filter((feature) => !feature.comingSoon);
  const comingSoon = plan.features.filter((feature) => feature.comingSoon);
  return (
    <article
      dir="ltr"
      className="group flex w-full max-w-[320px] flex-col overflow-clip rounded-[32px] border border-grey-100 bg-white">
      <PlanCardArt art={plan.art} promotion={plan.promotion} />
      <div className="flex flex-1 flex-col px-5 pt-5 pb-8">
        <h2 className="text-h4 font-semibold text-grey-1000">{plan.name}</h2>
        <p className="mt-2 text-[15px] leading-[1.32] text-grey-500">{plan.tagline}</p>
        <p
          dir="ltr"
          className="mt-3 flex items-center gap-2 text-[15px] leading-[1.32] tracking-[0.27px] text-grey-1000">
          {plan.regularPrice && <del className="text-grey-500">{plan.regularPrice}</del>}
          {plan.price}
        </p>
        <FeatureList features={included} />
        {comingSoon.length > 0 && (
          <>
            <p className="-mt-3 mb-6 text-[13px] leading-[1.32] font-medium text-grey-500">
              {ui.pricing.comingSoon}
            </p>
            <FeatureList features={comingSoon} muted />
          </>
        )}
        <ButtonLink href={getAppCta.href} size="md" className="mx-auto mt-auto">
          {plan.cta}
        </ButtonLink>
      </div>
    </article>
  );
}

/** The plans page: a card per plan and a link to the full price list (Figma 1030:18746). */
export function PricingPlans() {
  const { pricingPlans, ui } = useContent();
  const localizedPathFor = useLocalizedPath();
  return (
    <Section className="!pt-20 !pb-[124px]">
      <div className="grid justify-items-center gap-6 md:grid-cols-3 md:justify-center lg:grid-cols-[repeat(3,320px)]">
        {pricingPlans.map((plan) => (
          <PlanCardItem key={plan.name} plan={plan} />
        ))}
      </div>
      <div className="mt-12 flex justify-center">
        <Link
          to={localizedPathFor('/pricing')}
          className="text-[16px] leading-[1.4] font-medium tracking-[0.192px] text-grey-1000 underline transition-colors hover:text-red-500">
          {ui.pricing.seeFullPricing}
        </Link>
      </div>
    </Section>
  );
}
